import { jsPDF } from "jspdf";

// A4 dimensions in points (1 pt = 1/72 inch)
const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const MARGIN_TOP = 56;
const MARGIN_BOTTOM = 56;
const MARGIN_LEFT = 56;
const MARGIN_RIGHT = 56;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT;

const FONT_SIZE_BODY = 11;
const FONT_SIZE_SPEAKER = 11;
const LINE_HEIGHT_FACTOR = 1.55;
const SPEAKER_GAP = 6;
const PARAGRAPH_GAP = 10;

type SpeakerGroup = {
  speaker: string;
  words?: { type: string; text: string; confidence?: number }[];
};

async function fetchFontAsBase64(path: string) {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`PDF fontu yuklenemedi: ${path}`);
  }

  const bytes = new Uint8Array(await response.arrayBuffer());
  const chunkSize = 0x8000;
  let binary = "";

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)));
  }

  return btoa(binary);
}

async function registerFonts(doc: jsPDF) {
  const baseUrl = process.env.PUBLIC_URL || "";
  const [arialRegular, arialBold] = await Promise.all([
    fetchFontAsBase64(`${baseUrl}/fonts/Arial.ttf`),
    fetchFontAsBase64(`${baseUrl}/fonts/Arial-Bold.ttf`),
  ]);

  doc.addFileToVFS("Arial.ttf", arialRegular);
  doc.addFont("Arial.ttf", "Arial", "normal");
  doc.addFileToVFS("Arial-Bold.ttf", arialBold);
  doc.addFont("Arial-Bold.ttf", "Arial", "bold");
}

function extractText(group: SpeakerGroup): string {
  let text = "";
  group.words?.forEach((w) => {
    if (w.type === "word") text += (text ? " " : "") + w.text;
    else if (w.type === "punctuation") text += w.text;
  });
  return text;
}

function normalizePdfText(value: string): string {
  return value
    .replace(/\s+/g, " ")
    .replace(/\s+([.,!?;:])/g, "$1")
    .trim();
}

export interface PdfExportInput {
  groups: SpeakerGroup[];
  plainText?: string;
  fileName: string;
  speakerNames?: ReadonlyMap<string, string>;
}

export async function generateTranscriptPdf({ groups, plainText, fileName, speakerNames }: PdfExportInput) {
  const doc = new jsPDF({ unit: "pt", format: "a4", compress: true });
  await registerFonts(doc);
  doc.setFont("Arial", "normal");

  let cursorY = MARGIN_TOP;

  const lineHeightPt = FONT_SIZE_BODY * LINE_HEIGHT_FACTOR;

  function addNewPage() {
    doc.addPage();
    cursorY = MARGIN_TOP;
  }

  function ensureSpace(needed: number) {
    if (cursorY + needed > PAGE_HEIGHT - MARGIN_BOTTOM) {
      addNewPage();
    }
  }

  function writeSpeakerLabel(speaker: string) {
    if (cursorY > MARGIN_TOP + 1) {
      cursorY += SPEAKER_GAP;
    }
    ensureSpace(lineHeightPt + 2);

    doc.setFont("Arial", "bold");
    doc.setFontSize(FONT_SIZE_SPEAKER);
    doc.text(`[${speakerNames?.get(speaker) || speaker || "Konusmaci"}]:`, MARGIN_LEFT, cursorY);
    cursorY += lineHeightPt;
  }

  function writeParagraph(text: string) {
    const normalized = normalizePdfText(text);
    if (!normalized) return;

    doc.setFont("Arial", "normal");
    doc.setFontSize(FONT_SIZE_BODY);

    const lines: string[] = doc.splitTextToSize(normalized, CONTENT_WIDTH);

    for (let i = 0; i < lines.length; i++) {
      ensureSpace(lineHeightPt);
      doc.text(lines[i], MARGIN_LEFT, cursorY);
      cursorY += lineHeightPt;
    }

    cursorY += PARAGRAPH_GAP;
  }

  function writeSpeakerBlock(speaker: string, text: string) {
    // Prevent orphaned speaker labels at the bottom of a page
    const minBlockHeight = SPEAKER_GAP + lineHeightPt * 2;
    if (cursorY > MARGIN_TOP + 1 && cursorY + minBlockHeight > PAGE_HEIGHT - MARGIN_BOTTOM) {
      addNewPage();
    }

    writeSpeakerLabel(speaker);
    writeParagraph(text);
  }

  if (groups.length > 0) {
    groups.forEach((group) => {
      const text = extractText(group);
      writeSpeakerBlock(group.speaker, text);
    });
  } else if (plainText) {
    const speakerRegex = /^\[([^\]]+)\]:\s*$/;
    let currentSpeaker = "Konusmaci";
    let currentText = "";

    plainText.split("\n").forEach((line) => {
      const match = line.trim().match(speakerRegex);
      if (match) {
        if (currentText.trim()) {
          writeSpeakerBlock(currentSpeaker, currentText);
        }
        currentSpeaker = match[1];
        currentText = "";
        return;
      }
      currentText += `${currentText ? " " : ""}${line}`;
    });

    if (currentText.trim()) {
      writeSpeakerBlock(currentSpeaker, currentText);
    }
  } else {
    doc.setFont("Arial", "normal");
    doc.setFontSize(FONT_SIZE_BODY);
    doc.text("Transkript bulunamadi.", MARGIN_LEFT, cursorY);
  }

  doc.save(fileName);
}
